import AllInclusiveRounded from "@mui/icons-material/AllInclusiveRounded";
import CalendarMonth from "@mui/icons-material/CalendarMonth";
import FactCheckOutlined from "@mui/icons-material/FactCheckOutlined";
import TodayRounded from "@mui/icons-material/TodayRounded";
import { Box, Button, Typography } from "@mui/material";
import { toZonedTime } from "date-fns-tz";
import { TimelinePill } from "../../../modules/timeline/components/timeline-pill";
import { formatInterval } from "../../../utils/interval";
import { formatDatetime } from "../../../utils/iso8601";

import type { INTERVAL, ISO8601 } from "../../../utils/iso8601";
import type { MomentCardMoment } from "../types";
import type { FC, ReactElement, ReactNode } from "react";

export type MomentWhenClassification =
  | {
      type: "immediate";
      publishedAt: ISO8601 | null;
      timezone: string | null;
    }
  | {
      type: "scheduled";
      when: string;
      timezone: string | null;
    }
  | {
      type: "triggered";
      interval: INTERVAL | null;
      eventKey: string;
      followUp?: MomentCardMoment["follow_up"];
    }
  | {
      type: "survey-completed";
    }
  | {
      type: "unscheduled";
      timezone: string | null;
    };

export interface WhenProps {
  moment: {
    immediate: boolean | null;
    published_at: ISO8601 | null;
    timezone: string | null;
    trigger_survey_id?: string | null;
    schedule?: {
      when: string | null;
      event_key: string | null;
      interval: INTERVAL | null;
    } | null;
    follow_up?: MomentCardMoment["follow_up"];
  };
}

export function classifyMomentWhen(moment?: WhenProps["moment"]): MomentWhenClassification {
  // Priority 1: Immediate
  if (moment?.immediate) {
    return {
      type: "immediate",
      publishedAt: moment.published_at,
      timezone: moment.timezone,
    };
  }

  // Priority 2: Scheduled (Specific Time)
  if (moment?.schedule?.when) {
    return {
      type: "scheduled",
      when: moment.schedule.when,
      timezone: moment.timezone,
    };
  }

  // Priority 3: Triggered (Event based)
  if (moment?.schedule?.event_key) {
    return {
      type: "triggered",
      eventKey: moment.schedule.event_key,
      interval: moment.schedule.interval,
      followUp: moment.follow_up,
    };
  }

  // Priority 4: Survey completed trigger
  if (moment?.trigger_survey_id) {
    return { type: "survey-completed" };
  }

  return {
    type: "unscheduled",
    timezone: moment?.timezone ?? null,
  };
}

export function renderMomentWhenIcon(c: MomentWhenClassification): ReactElement {
  const sx = { verticalAlign: "middle", mr: 1 };

  if (c.type === "triggered") {
    return <AllInclusiveRounded sx={sx} />;
  }

  if (c.type === "survey-completed") {
    return <FactCheckOutlined sx={sx} />;
  }

  // For Immediate, Scheduled, and Unscheduled, we fall back to timezone logic
  if (c.timezone) return <TodayRounded sx={sx} />;

  return <CalendarMonth sx={sx} />;
}

export function renderMomentWhenContent(c: MomentWhenClassification): ReactNode {
  switch (c.type) {
    case "immediate": {
      if (!c.publishedAt) return "Not scheduled";
      const userTimeZone = Intl.DateTimeFormat().resolvedOptions().timeZone;
      return `${formatDatetime(new Date(toZonedTime(c.publishedAt, userTimeZone)))} (${userTimeZone})`;
    }

    case "scheduled": {
      const timezoneLabel = c.timezone ? ` (${c.timezone})` : " (Recipient’s local time zone)";
      return formatDatetime(new Date(c.when)) + timezoneLabel;
    }

    case "triggered": {
      const href = c.followUp
        ? `/moment/${c.followUp.config.source_id}`
        : `/timeline/${c.eventKey}`;

      const label = c.followUp ? c.followUp.config.moment_title : c.eventKey;

      const analyticsId = c.followUp ? "moment-card-moment-pill" : "moment-card-event-pill";

      return (
        <Box sx={{ display: "inline-block" }} component="span">
          {formatInterval(c.interval)}&nbsp;
          <Button data-analytics-id={analyticsId} href={href} sx={{ m: 0, p: 0 }}>
            <TimelinePill>{label}</TimelinePill>
          </Button>
        </Box>
      );
    }

    case "survey-completed":
      return "Survey triggered";

    case "unscheduled":
    default:
      return "Not scheduled";
  }
}

export const When: FC<WhenProps> = ({ moment }) => {
  const classification = classifyMomentWhen(moment);

  return (
    <>
      <Typography align="left" fontSize={12} color="rgb(180, 180, 180)">
        When
      </Typography>
      <Box data-testid="when" sx={{ display: "inline-block" }}>
        {renderMomentWhenIcon(classification)}
        {renderMomentWhenContent(classification)}
      </Box>
    </>
  );
};
